import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../ui';

const QuickActions = ({ variant = 'default', onActionClick }) => {
  const navigate = useNavigate();
  const { tenantType } = useAuth();
  const isMedical = tenantType === 'cabinet_medical';

  const actions = {
    default: [
      {
        id: 'new-invoice',
        label: isMedical ? "Nouvelle Note d'honoraire" : 'Nouvelle Facture',
        icon: 'IconFileInvoice',
        color: 'blue',
        path: '/invoices/new',
        description: isMedical ? "Créer une note d'honoraire" : 'Créer une facture manuelle',
        shortcut: 'Ctrl + I'
      },
      {
        id: 'new-client',
        label: 'Nouveau Client',
        icon: 'IconUserPlus',
        color: 'green',
        path: '/clients/new',
        description: 'Ajouter un nouveau client',
        shortcut: 'Ctrl + C'
      },
      {
        id: 'new-payment',
        label: 'Enregistrer Paiement',
        icon: 'IconCash',
        color: 'purple',
        path: '/payments/new',
        description: 'Enregistrer un paiement client',
        shortcut: 'Ctrl + P'
      },
      {
        id: 'generate-auto',
        label: isMedical ? "Générer Notes d'honoraires Auto" : 'Générer Factures Auto',
        icon: 'IconRobot',
        color: 'orange',
        path: '/invoices/auto',
        description: isMedical ? "Générer les notes d'honoraires automatiques" : 'Générer les factures automatiques',
        shortcut: 'Ctrl + A'
      },
      {
        id: 'reports',
        label: 'Rapports',
        icon: 'IconChartBar',
        color: 'indigo',
        path: '/reports',
        description: 'Voir les rapports détaillés',
        shortcut: 'Ctrl + R'
      },
      {
        id: 'export-data',
        label: 'Exporter Données',
        icon: 'IconFileExport',
        color: 'red',
        path: '/export',
        description: 'Exporter en PDF/Excel',
        shortcut: 'Ctrl + E'
      }
    ],
    admin: [
      {
        id: 'settings',
        label: 'Paramètres',
        icon: 'IconSettings',
        color: 'gray',
        path: '/admin/backup',
        description: 'Paramètres du système'
      },
      {
        id: 'users',
        label: 'Gérer Utilisateurs',
        icon: 'IconUsersGroup',
        color: 'gray',
        path: '/users',
        description: 'Gestion des utilisateurs'
      },
      {
        id: 'backup',
        label: 'Sauvegarde',
        icon: 'IconDatabase',
        color: 'gray',
        path: '/admin/backup',
        description: 'Sauvegarde des données'
      }
    ],
    collaborator: [
      {
        id: 'my-tasks',
        label: 'Mes Tâches',
        icon: 'IconTask',
        color: 'blue',
        path: '/tasks',
        description: 'Voir mes tâches assignées'
      },
      {
        id: 'my-clients',
        label: 'Mes Clients',
        icon: 'IconUsers',
        color: 'green',
        path: '/clients?my=true',
        description: 'Voir mes clients assignés'
      },
      {
        id: 'calendar',
        label: 'Calendrier',
        icon: 'IconCalendar',
        color: 'purple',
        path: '/calendar',
        description: 'Voir mon calendrier'
      }
    ]
  };

  const currentActions = actions[variant] || actions.default;

  const handleActionClick = (action) => {
    if (onActionClick) {
      onActionClick(action);
    }
    if (action.path) {
      navigate(action.path);
    }
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 hover:bg-blue-200',
    green: 'bg-green-100 text-green-600 hover:bg-green-200',
    purple: 'bg-purple-100 text-purple-600 hover:bg-purple-200',
    orange: 'bg-orange-100 text-orange-600 hover:bg-orange-200',
    indigo: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200',
    red: 'bg-red-100 text-red-600 hover:bg-red-200',
    gray: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Actions rapides</h3>
          <p className="text-sm text-gray-500">Accédez rapidement aux fonctionnalités principales</p>
        </div>

        {variant === 'default' && (
          <button 
            onClick={() => navigate('/actions')}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            Voir toutes les actions
            <Icon name="IconArrowRight" size={12} className="ml-1" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
        {currentActions.map((action) => (
          <button
            key={action.id}
            onClick={() => handleActionClick(action)}
            className={`
              flex flex-col items-center justify-center p-4 rounded-xl
              transition-all duration-200 transform hover:-translate-y-1
              ${colorClasses[action.color]}
              hover:shadow-md
              group relative
            `}
          >
            {action.shortcut && (
              <div className="absolute -top-2 -right-2">
                <span className="text-xs bg-gray-800 text-white px-2 py-1 rounded">
                  {action.shortcut}
                </span>
              </div>
            )}

            <div className="relative">
              <Icon name={action.icon} size={24} className="mb-3" />
              <div className="absolute inset-0 bg-current opacity-10 rounded-full scale-0 group-hover:scale-125 transition-transform duration-300"></div>
            </div>

            <span className="text-sm font-medium text-center mb-1">
              {action.label}
            </span>

            <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
                {action.description}
                {action.shortcut && (
                  <div className="text-gray-300 mt-1">
                    Raccourci: {action.shortcut}
                  </div>
                )}
              </div>
              <div className="w-3 h-3 bg-gray-900 transform rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2"></div>
            </div>
          </button>
        ))}
      </div>

      {variant === 'default' && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Actions fréquentes</h4>
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => navigate('/invoices?status=sent')}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {isMedical ? "Notes d'honoraires envoyées" : 'Factures envoyées'}
            </button>
            <button 
              onClick={() => navigate('/clients?balance>0')}
              className="px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
            >
              Clients en retard
            </button>
            <button 
              onClick={() => navigate('/payments/today')}
              className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
            >
              Paiements aujourd`&aposhui
            </button>
            <button 
              onClick={() => navigate('/invoices?status=draft')}
              className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
            >
              Brouillons
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

QuickActions.propTypes = {
  variant: PropTypes.oneOf(['default', 'admin', 'collaborator']),
  onActionClick: PropTypes.func
};

export default QuickActions;

